import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface CreateEditGraphModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: GraphFormData) => void;
  mode: 'create' | 'edit';
  saving?: boolean;
  saveError?: string | null;
}

export interface GraphFormData {
  id: string;
  nodes: string[];
  tables: string[];
  baseTable: string;
  joinCondition: string | null;
  businessRules: string | null;
}

const CreateEditGraphModal: React.FC<CreateEditGraphModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving = false,
  saveError = null,
}) => {
  const { selectedGraph, loading } = useAppSelector((state) => state.graphs);

  const [id, setId] = useState('');
  const [nodes, setNodes] = useState('');
  const [tables, setTables] = useState('');
  const [baseTable, setBaseTable] = useState('');
  const [joinCondition, setJoinCondition] = useState('');
  const [businessRules, setBusinessRules] = useState('');

  useEffect(() => {
    if (mode === 'edit' && selectedGraph) {
      setId(selectedGraph.id);
      setNodes(selectedGraph.nodes?.join(', ') || '');
      setTables(selectedGraph.tables?.join(', ') || '');
      setBaseTable(selectedGraph.baseTable || '');
      setJoinCondition(selectedGraph.joinCondition || '');
      setBusinessRules(selectedGraph.businessRules || '');
    } else if (mode === 'create') {
      setId('');
      setNodes('');
      setTables('');
      setBaseTable('');
      setJoinCondition('');
      setBusinessRules('');
    }
  }, [mode, selectedGraph, open]);

  const handleSave = () => {
    const nodesArray = nodes
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    const tablesArray = tables
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const formData: GraphFormData = {
      id,
      nodes: nodesArray,
      tables: tablesArray,
      baseTable,
      joinCondition: joinCondition || null,
      businessRules: businessRules || null,
    };

    onSave(formData);
  };

  const isFormValid = id && nodes && tables && baseTable;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nuevo Grafo' : 'Editar Grafo'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="ID"
              value={id}
              onChange={(e) => setId(e.target.value)}
              margin="normal"
              required
              disabled={saving || mode === 'edit'}
            />

            <TextField
              fullWidth
              label="Nodos (separados por coma)"
              value={nodes}
              onChange={(e) => setNodes(e.target.value)}
              margin="normal"
              required
              disabled={saving}
              helperText="Ingrese los nodos separados por coma"
            />

            <TextField
              fullWidth
              label="Tablas (separadas por coma)"
              value={tables}
              onChange={(e) => setTables(e.target.value)}
              margin="normal"
              required
              disabled={saving}
              helperText="Ingrese las tablas separadas por coma"
            />

            <TextField
              fullWidth
              label="Tabla Base"
              value={baseTable}
              onChange={(e) => setBaseTable(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Condición de Join"
              value={joinCondition}
              onChange={(e) => setJoinCondition(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Reglas de Negocio"
              value={businessRules}
              onChange={(e) => setBusinessRules(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !isFormValid}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditGraphModal;
